import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PlotlyModule } from 'angular-plotly.js';
import { MainNavbarComponent } from '../../../core/components/main-navbar/main-navbar.component';
import { SiaStatisticsService, UserStats, DailyActivity, TabDetail } from './sia-statistics.service';

@Component({
  selector: 'app-sia-statistics',
  standalone: true,
  imports: [CommonModule, FormsModule, PlotlyModule, MainNavbarComponent],
  templateUrl: './sia-statistics.component.html',
  styleUrl: './sia-statistics.component.css'
})
export class SiaStatisticsComponent implements OnInit {
  loading = true;
  error: string | null = null;

  users: UserStats[] = [];
  dailyActivity: DailyActivity[] = [];

  // Filters
  dateFrom: string | null = null;
  dateTo: string = new Date().toISOString().split('T')[0];
  excludeQuantipUsers = true;
  userSearch = '';

  // Expanded user detail
  expandedUserId: number | null = null;
  expandedUserTabs: TabDetail[] = [];
  loadingTabs = false;

  // Charts
  activityChart: any = null;
  userActivityChart: any = null;
  chartConfig = { responsive: true, displayModeBar: false };

  constructor(private siaStatsService: SiaStatisticsService) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.error = null;
    this.expandedUserId = null;
    this.expandedUserTabs = [];
    this.userActivityChart = null;

    this.siaStatsService.getOverview(this.dateFrom, this.dateTo, this.excludeQuantipUsers).subscribe({
      next: (response) => {
        this.users = response.payload.users;
        this.dailyActivity = response.payload.daily_activity;
        this.buildActivityChart();
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load statistics';
        this.loading = false;
        console.error(err);
      }
    });
  }

  // --- Filters ---

  filteredUsers(): UserStats[] {
    const q = (this.userSearch || '').trim().toLowerCase();
    if (!q) return this.users;
    return this.users.filter(u =>
      u.username.toLowerCase().includes(q) ||
      u.organisation.toLowerCase().includes(q)
    );
  }

  onFilterChange(): void {
    this.loadData();
  }

  clearDateFilter(): void {
    this.dateFrom = null;
    this.dateTo = new Date().toISOString().split('T')[0];
    this.loadData();
  }

  getToday(): string {
    return new Date().toISOString().split('T')[0];
  }

  clearSearch(): void {
    this.userSearch = '';
  }

  // --- Overall daily activity chart ---

  buildActivityChart(): void {
    if (!this.dailyActivity || this.dailyActivity.length === 0) {
      this.activityChart = null;
      return;
    }

    const dates = this.dailyActivity.map(d => d.date);
    const counts = this.dailyActivity.map(d => d.count);

    // Build hover text showing user breakdown
    const hoverTexts = this.dailyActivity.map(d => {
      const lines = d.users.map(u => `${u.username}: ${u.count}`);
      return `${d.date}<br>Total: ${d.count}<br>${lines.join('<br>')}`;
    });

    this.activityChart = {
      data: [{
        x: dates,
        y: counts,
        type: 'bar',
        marker: { color: '#5e1075' },
        hovertext: hoverTexts,
        hoverinfo: 'text'
      }],
      layout: {
        title: 'Daily Activity (All Users)',
        xaxis: { title: 'Date', type: 'date' },
        yaxis: { title: 'Searches', dtick: 1 },
        autosize: true,
        margin: { t: 40, b: 60, l: 50, r: 20 },
        bargap: 0.3
      }
    };
  }

  // --- User expansion ---

  toggleUser(userId: number): void {
    if (this.expandedUserId === userId) {
      this.expandedUserId = null;
      this.expandedUserTabs = [];
      this.userActivityChart = null;
      return;
    }

    this.expandedUserId = userId;
    this.expandedUserTabs = [];
    this.userActivityChart = null;
    this.loadingTabs = true;

    this.siaStatsService.getUserTabs(userId, this.dateFrom, this.dateTo).subscribe({
      next: (response) => {
        this.expandedUserTabs = response.payload;
        this.buildUserActivityChart();
        this.loadingTabs = false;
      },
      error: (err) => {
        console.error('Failed to load user tabs:', err);
        this.loadingTabs = false;
      }
    });
  }

  buildUserActivityChart(): void {
    if (!this.expandedUserTabs || this.expandedUserTabs.length === 0) {
      this.userActivityChart = null;
      return;
    }

    // Group by date
    const dateMap: { [date: string]: number } = {};
    for (const tab of this.expandedUserTabs) {
      if (tab.created_at) {
        dateMap[tab.created_at] = (dateMap[tab.created_at] || 0) + 1;
      }
    }

    const dates = Object.keys(dateMap).sort();
    const counts = dates.map(d => dateMap[d]);

    if (dates.length === 0) {
      this.userActivityChart = null;
      return;
    }

    this.userActivityChart = {
      data: [{
        x: dates,
        y: counts,
        type: 'bar',
        marker: { color: '#96c242' }
      }],
      layout: {
        title: 'User Activity Over Time',
        xaxis: { title: 'Date', type: 'date' },
        yaxis: { title: 'Tabs Created', dtick: 1 },
        autosize: true,
        margin: { t: 40, b: 60, l: 50, r: 20 },
        bargap: 0.3
      }
    };
  }
}
